// Sales & payouts (docs/PHASE7-CONTRACT.md "Partner Hub"): a period, the statement Ops sees
// (gross / commission / adjustments / net, money.shopStatement on the server), daily bars, top
// products and the shop's payouts. Dates are Manila calendar days 'YYYY-MM-DD', inclusive.
// Pure: no DOM, no Intl.

import { FIRST_PARTY_LABEL, isFirstParty, peso } from '../../../lib/format.js';

/** @typedef {import('@popcart/core/v1').ShopSales} ShopSales */
/** @typedef {import('@popcart/core/v1').ShopPayout} ShopPayout */
/** @typedef {import('@popcart/core/v1').PayoutStatement} PayoutStatement */
/** @typedef {'thisWeek' | 'lastWeek' | 'thisMonth' | 'custom'} Preset */

/** @type {{ value: Preset; label: string }[]} */
export const PRESETS = [
  { value: 'thisWeek', label: 'This week' },
  { value: 'lastWeek', label: 'Last week' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'custom', label: 'Custom' },
];

export const MAX_DAYS = 92;
const MANILA_MS = 8 * 3600 * 1000;
const DAY_MS = 86_400_000;
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

/** ms (UTC midnight of that calendar day) → 'YYYY-MM-DD'. @param {number} ms */
function fmt(ms) {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

/** 'YYYY-MM-DD' → UTC ms of that calendar day, or NaN. @param {string} day */
function parse(day) {
  const m = DAY_RE.exec(String(day));
  if (!m) return NaN;
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return fmt(ms) === day ? ms : NaN;
}

/** Today in Manila. @param {number} now ms */
export function manilaToday(now) {
  const w = new Date(now + MANILA_MS);
  return fmt(Date.UTC(w.getUTCFullYear(), w.getUTCMonth(), w.getUTCDate()));
}

/** @param {string} day @param {number} n */
export function addDays(day, n) {
  return fmt(parse(day) + n * DAY_MS);
}

/** Is this a real calendar day? @param {string} day */
export function isDay(day) {
  return Number.isFinite(parse(day));
}

/** Inclusive day count. @param {string} from @param {string} to */
export function daySpan(from, to) {
  return Math.round((parse(to) - parse(from)) / DAY_MS) + 1;
}

/**
 * The preset's period, Monday-first weeks, ending today for the running ones.
 * @param {Exclude<Preset, 'custom'>} preset
 * @param {number} now ms
 */
export function presetRange(preset, now) {
  const today = manilaToday(now);
  const dow = new Date(parse(today)).getUTCDay(); // 0 Sun … 6 Sat
  const monday = addDays(today, -((dow + 6) % 7));
  if (preset === 'lastWeek') return { from: addDays(monday, -7), to: addDays(monday, -1) };
  if (preset === 'thisMonth') return { from: `${today.slice(0, 8)}01`, to: today };
  return { from: monday, to: today };
}

/**
 * A custom period's problem, or null.
 * @param {string} from @param {string} to @param {number} now ms
 */
export function rangeProblem(from, to, now) {
  if (!isDay(from) || !isDay(to)) return 'Pick a start and an end date.';
  if (from > to) return 'The start date comes after the end date.';
  if (from > manilaToday(now)) return 'That period hasn’t started yet.';
  if (daySpan(from, to) > MAX_DAYS) return `Pick ${MAX_DAYS} days or fewer.`;
  return null;
}

/** "5 Oct". @param {string} day */
export function shortDay(day) {
  const ms = parse(day);
  if (!Number.isFinite(ms)) return day;
  const d = new Date(ms);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** "29 Sep – 5 Oct" (the year when it isn't this one). @param {string} from @param {string} to @param {number} [now] */
export function periodLabel(from, to, now = Date.now()) {
  const year = manilaToday(now).slice(0, 4);
  const y = (/** @type {string} */ d) => (d.slice(0, 4) !== year ? ` ${d.slice(0, 4)}` : '');
  if (from === to) return `${shortDay(from)}${y(from)}`;
  return `${shortDay(from)}${y(from)} – ${shortDay(to)}${y(to)}`;
}

/**
 * The four statement cards. Commission shows as money out (−); adjustments keep their sign
 * (negative = the shop owes it, e.g. an absorbed catch-weight overrun).
 * A first-party shop's commission line says so in words (Phase 7 decision 2), never "0% of gross".
 * @param {PayoutStatement & { firstParty?: boolean }} s
 */
export function statementCards(s) {
  const pct = s.commissionBps / 100;
  return [
    { key: 'gross', label: 'Gross sales', value: peso(s.grossCentavos), detail: `${s.ordersCount} delivered ${s.ordersCount === 1 ? 'order' : 'orders'}` },
    {
      key: 'commission',
      label: 'Commission',
      value: s.commissionCentavos ? peso(-s.commissionCentavos) : peso(0),
      detail: isFirstParty(s) ? FIRST_PARTY_LABEL : `${Number.isInteger(pct) ? pct : pct.toFixed(2)}% of gross`,
    },
    { key: 'adjustments', label: 'Adjustments', value: peso(s.adjustmentsCentavos), detail: s.adjustmentsCentavos ? 'catch-weight and corrections' : 'none this period' },
    { key: 'net', label: 'Net to you', value: peso(s.netCentavos), detail: s.payoutMethod ? `paid by ${s.payoutMethod}` : 'gross − commission + adjustments' },
  ];
}

/**
 * Daily bars: height as a % of the best day (0 days get no fill). Labels "MON 5".
 * @param {ShopSales['days']} days
 */
export function dayBars(days) {
  const max = days.reduce((m, d) => Math.max(m, d.grossCentavos), 0);
  return days.map((d) => {
    const ms = parse(d.day);
    const wd = Number.isFinite(ms) ? WEEKDAYS[new Date(ms).getUTCDay()] : '';
    return {
      day: d.day,
      label: `${wd} ${Number.isFinite(ms) ? new Date(ms).getUTCDate() : ''}`.trim(),
      pct: max ? Math.round((d.grossCentavos / max) * 100) : 0,
      value: peso(d.grossCentavos),
      orders: d.orders,
      aria: `${shortDay(d.day)}: ${peso(d.grossCentavos)}, ${d.orders} ${d.orders === 1 ? 'order' : 'orders'}`,
    };
  });
}

/** Share of the period's gross, for the top-products bars. @param {ShopSales['topProducts']} top */
export function topBars(top) {
  const max = top.reduce((m, p) => Math.max(m, p.grossCentavos), 0);
  return top.map((p) => ({ ...p, pct: max ? Math.round((p.grossCentavos / max) * 100) : 0, value: peso(p.grossCentavos) }));
}

/** Status word + tone for a payout. */
export const PAYOUT_VIEW = /** @type {const} */ ({
  pending: { label: 'Pending', tone: 'warning' },
  paid: { label: 'Paid', tone: 'success' },
  void: { label: 'Void', tone: 'neutral' },
});

/** "Paid 6 Oct" / "Waiting to be paid" / "Cancelled". @param {ShopPayout} p */
export function payoutNote(p) {
  if (p.status === 'paid') return p.paidAt ? `Paid ${shortDay(manilaToday(Date.parse(p.paidAt)))}` : 'Paid';
  if (p.status === 'void') return 'Cancelled by PopCart';
  return 'Waiting to be paid';
}

/** No delivered orders in the period? @param {ShopSales} s */
export function isEmptyPeriod(s) {
  return s.statement.ordersCount === 0 && s.days.every((d) => d.orders === 0);
}

/**
 * The client call the page makes (tests pass a fake api).
 * @param {Pick<import('@popcart/core/v1').PopCartApi, 'shopSales'>} api
 * @param {string} shopId
 */
export function salesActions(api, shopId) {
  return {
    /** @param {{ from: string; to: string }} range */
    load: (range) => api.shopSales(shopId, { from: range.from, to: range.to }),
  };
}
